import React, { useEffect } from "react";
import {
  MapPin, Clock3, MessageCircle, Phone, Navigation, CalendarDays,
  ArrowUpRight, Home, Sparkles
} from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import styles from "../styles/Contact.module.css";

const phone = "+2349165387780";
const whatsapp = "2349165387780";
const address = "Shop H9, Ogombo Road, opposite Lekki Scheme 2, off Abraham Adesanya, Ajah, Lagos";
const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export default function Contact({ onBookClick }) {
  useEffect(() => {
    document.title = "Contact Dey Fresh Signature | Ajah, Lagos";

    const nodes = document.querySelectorAll('[data-contact-reveal]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.shell}>
          <div className={styles.eyebrow}><span /> CONTACT DEY FRESH</div>
          <div className={styles.heroGrid}>
            <div>
              <h1>Come in.<br/><em>Leave fresh.</em></h1>
            </div>
            <div className={styles.heroCopy}>
              <p>Ready for your next look? Visit our Ajah salon, reserve an appointment, or speak with us directly on WhatsApp.</p>
              <div className={styles.heroActions}>
                <button onClick={() => onBookClick?.()} className={styles.primaryBtn}>
                  Book appointment <CalendarDays size={17}/>
                </button>
                <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className={styles.textLink}>
                  WhatsApp us <ArrowUpRight size={16}/>
                </a>
              </div>
            </div>
          </div>
          <div className={styles.heroMeta}>
            <span>AJAH · LAGOS</span><span>HAIR · GROOMING · BEAUTY</span><span>HOME SERVICE AVAILABLE</span>
          </div>
        </div>
      </section>

      <section className={styles.quickSection}>
        <div className={styles.shell}>
          <div className={styles.quickGrid}>
            <a data-contact-reveal href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className={styles.quickCard}>
              <span className={styles.icon}><MessageCircle size={20}/></span>
              <div><small>FASTEST RESPONSE</small><h3>WhatsApp us</h3><p>Questions, enquiries and home-service requests.</p></div>
              <ArrowUpRight size={18}/>
            </a>
            <a data-contact-reveal href={`tel:${phone}`} className={styles.quickCard}>
              <span className={styles.icon}><Phone size={20}/></span>
              <div><small>CALL THE SALON</small><h3>0916 538 7780</h3><p>Speak directly with the Dey Fresh team.</p></div>
              <ArrowUpRight size={18}/>
            </a>
            <a data-contact-reveal href={mapUrl} target="_blank" rel="noreferrer" className={styles.quickCard}>
              <span className={styles.icon}><Navigation size={20}/></span>
              <div><small>GET DIRECTIONS</small><h3>Find us in Ajah</h3><p>Open the salon location in Google Maps.</p></div>
              <ArrowUpRight size={18}/>
            </a>
          </div>
        </div>
      </section>

      <section className={styles.visitSection}>
        <div className={styles.shell}>
          <div className={styles.visitGrid}>
            <div data-contact-reveal className={styles.visitCopy}>
              <div className={styles.sectionTag}>VISIT THE SALON</div>
              <h2>Your fresh look<br/>starts <em>here.</em></h2>
              <p className={styles.lead}>A premium unisex salon in Ajah for hair, barbering, locs, nails, pedicure, lashes and facial care.</p>

              <div className={styles.detailBlock}>
                <MapPin size={20}/>
                <div><small>ADDRESS</small><strong>Shop H9, Ogombo Road</strong><p>Opp. Lekki Scheme 2, off Abraham Adesanya, Ajah, Lagos.</p></div>
              </div>
              <div className={styles.detailBlock}>
                <Clock3 size={20}/>
                <div><small>OPENING HOURS</small><strong>Mon–Sat · 9:00 AM – 8:00 PM</strong><p>Sunday · 1:00 PM – 8:00 PM</p></div>
              </div>
              <a href={mapUrl} target="_blank" rel="noreferrer" className={styles.directionBtn}>Get directions <Navigation size={16}/></a>
            </div>

            <div data-contact-reveal className={styles.mapWrap}>
              <iframe
                title="Dey Fresh Signature location"
                src="https://www.google.com/maps?q=Ogombo%20Road%20Abraham%20Adesanya%20Ajah%20Lagos&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className={styles.mapCard}>
                <span><MapPin size={16}/></span>
                <div><small>DEY FRESH SIGNATURE</small><strong>Ogombo Road, Ajah</strong></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.contactExperience}>
        <div className={styles.shell}>
          <div data-contact-reveal className={styles.experienceHead}>
            <div><div className={styles.sectionTag}>HOW WE CAN HELP</div><h2>Choose what works<br/>best for <em>you.</em></h2></div>
            <p>Whether you want to visit the salon or enjoy your appointment at home, reaching us should be simple.</p>
          </div>
          <div className={styles.experienceGrid}>
            <article data-contact-reveal><span>01</span><CalendarDays/><h3>Book your visit</h3><p>Choose your service, date and preferred time through our appointment experience.</p><button onClick={() => onBookClick?.()}>Book now <ArrowUpRight size={15}/></button></article>
            <article data-contact-reveal><span>02</span><Home/><h3>Request home service</h3><p>Prefer us to come to you? Send your service and location details through WhatsApp.</p><a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent("Hello Dey Fresh Signature, I would like to enquire about home service.")}`} target="_blank" rel="noreferrer">Ask on WhatsApp <ArrowUpRight size={15}/></a></article>
            <article data-contact-reveal><span>03</span><Sparkles/><h3>Need help choosing?</h3><p>Tell us the look or treatment you have in mind and our team can guide you to the right service.</p><a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent("Hello Dey Fresh Signature, I need help choosing the right service.")}`} target="_blank" rel="noreferrer">Talk to us <ArrowUpRight size={15}/></a></article>
          </div>
        </div>
      </section>

      <section className={styles.finalCta}>
        <div className={styles.shell}>
          <div data-contact-reveal className={styles.finalInner}>
            <span className={styles.bigDf}>DF</span>
            <div><div className={styles.sectionTag}>READY WHEN YOU ARE</div><h2>Let's create your<br/><em>next look.</em></h2></div>
            <div className={styles.finalActions}>
              <button onClick={() => onBookClick?.()}>Book appointment <ArrowUpRight size={17}/></button>
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer"><MessageCircle size={18}/> WhatsApp</a>
            </div>
          </div>
          <div className={styles.socialNote}><FaInstagram size={15}/> Follow Dey Fresh Signature for our latest work and salon updates.</div>
        </div>
      </section>
    </main>
  );
}
